/**
 * Roll up WO / CO / Invoice line items by base name or part number.
 * Normally material + labor + freight for the same part → one row with a
 * combined total. When `breakout=true`, the same row carries `material_total`
 * and `labor_total` as separate buckets so the consumer can render them as
 * side-by-side columns (per user requirement: Breakout = columns, not extra
 * rows). Equipment, disposal and travel stay as standalone rows because
 * they have no matching labor counterpart to merge into.
 */
function dedupDescription(desc) {
  if (!desc) return desc;
  let cleaned = desc.replace(/\s*\[.*?\]\s*/g, "").trim();
  // Handle "X - X"
  const dashParts = cleaned.split(" - ");
  if (dashParts.length === 2 && dashParts[0].trim().toLowerCase() === dashParts[1].trim().toLowerCase()) {
    cleaned = dashParts[0].trim();
  }
  // Handle "Scope: Description" — remove scope prefix
  const colonIdx = cleaned.indexOf(": ");
  if (colonIdx > 0) {
    const detail = cleaned.substring(colonIdx + 2).trim();
    if (detail.length > 0) cleaned = detail;
  }
  return cleaned;
}

export function rollUpLineItems(lineItems, breakout = false, breakoutFreight = false) {
  const grouped = {};
  const standalone = [];

  (lineItems || []).forEach((item, srcIdx) => {
    const ct = String(item.cost_type || "").toLowerCase();
    const lineTotal = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
    // When a user has typed a custom name into the row's Name input
    // (`item.custom_name`), use it verbatim — never trim, never regex —
    // so trailing spaces / "(labor)" / etc. survive the rollup.
    const customName = (typeof item.custom_name === "string" && item.custom_name) ? item.custom_name : null;

    // Equipment, disposal, travel stay as separate rows
    if (
      ["equipment", "disposal", "travel", "3", "5"].includes(ct) ||
      ct.startsWith("equip") || ct.startsWith("disp") || ct.startsWith("trav")
    ) {
      const defaultPart = ct === "equipment" || ct === "5" ? "EQMT"
        : ct === "disposal" ? "DISP"
        : ct === "travel" ? "TRVL" : "";
      const rawDesc = dedupDescription(item.description || "");
      const baseName = rawDesc.replace(/\s*\((labor|freight)\)\s*$/i, "").trim();
      standalone.push({
        part_number: item.part_number || defaultPart,
        name: customName ?? (baseName || ct.charAt(0).toUpperCase() + ct.slice(1)),
        description: customName ?? (baseName || item.description || ""),
        quantity: item.quantity || 1,
        unit_price: item.unit_price || 0,
        total: lineTotal,
        material_total: lineTotal,
        labor_total: 0,
        cost_type: item.cost_type || "",
        // Indices of the flat `lineItems` that composed this row, so
        // editors can delete / re-qty the exact source rows instead of
        // re-deriving a match key (which drifted from the rollup key).
        source_indices: [srcIdx],
      });
      return;
    }

    const rawDesc = dedupDescription(item.description || "");
    const baseName = rawDesc.replace(/\s*\((labor|freight)\)\s*$/i, "").trim();
    const key = (item.part_number && item.part_number.trim()) || baseName || "Unknown";
    const isLabor = ct === "labor" || ct === "1";
    const isFreight = breakoutFreight && (ct === "freight" || ct === "4");
    if (!grouped[key]) {
      grouped[key] = {
        part_number: item.part_number || "",
        name: customName ?? (baseName || rawDesc || ""),
        description: customName ?? (baseName || rawDesc || ""),
        quantity: 0,
        unit_price: 0,
        total: 0,
        material_total: 0,
        labor_total: 0,
        freight_total: 0,
        cost_type: item.cost_type || "",
        source_indices: [],
      };
    }
    grouped[key].source_indices.push(srcIdx);
    // Use the material quantity as the display qty (it represents the actual part count)
    if (ct === "material" || ct === "2") {
      grouped[key].quantity = item.quantity || grouped[key].quantity || 1;
      grouped[key].unit_price = item.unit_price || grouped[key].unit_price || 0;
      grouped[key].has_material = true;
    } else if (!grouped[key].has_material) {
      // Labor-only / flat-rate / subcontract rows: carry the proposal quantity
      // instead of collapsing to qty 1 with the extended price as unit price.
      const pq = Number(item.proposal_qty) || 0;
      if (pq > 0) grouped[key].quantity = pq;
      else if (item.pricing_mode === "flat_rate" || item.pricing_mode === "subcontract") grouped[key].quantity = Number(item.quantity) || grouped[key].quantity;
    }
    if (item.part_number && !grouped[key].part_number) {
      grouped[key].part_number = item.part_number;
    }
    grouped[key].total += lineTotal;
    if (isLabor) grouped[key].labor_total += lineTotal;
    else if (isFreight) grouped[key].freight_total += lineTotal;
    else grouped[key].material_total += lineTotal;
  });

  const result = Object.values(grouped).map(g => {
    if (!g.quantity) g.quantity = 1;
    if (!g.unit_price) g.unit_price = g.quantity > 0 ? g.total / g.quantity : g.total;
    return g;
  });

  // Hide the split buckets when breakout is OFF so downstream renderers can
  // safely always check truthy `material_total`.
  if (!breakout) {
    return [...result, ...standalone].map(r => ({ ...r, material_total: r.total, labor_total: 0 }));
  }
  return [...result, ...standalone];
}

/**
 * Bake a CC processing fee % into each rolled row's `material_total` and
 * `total` so the customer-facing view never shows a mystery gap between
 * the line-item sum and the subtotal label. Distributes the fee
 * proportionally to each row's `material_total` (mirrors the proposal
 * `applyCCFeeDistribution` rule). When no row has any material, the fee
 * folds into the first row instead. INPUT must already be the rolled-up
 * items (call `rollUpLineItems` first); we never mutate raw `line_items`
 * because the BOM, Budget, Procurement and Invoice cost-analysis flows
 * read those directly and must stay fee-free.
 *
 * #173 (Group H): `feeOverride` (optional, in dollars) lets the WO PDF
 * pass the fully-computed `ccFeeAmount` — which is calculated against
 * `revisedSubtotal` (base + approved change orders) — so the line-item
 * distribution matches the same subtotal the displayed `ccFeeAmount`
 * was computed from. Without this, the helper would calculate
 * `subtotal * feePct/100` against the base subtotal only, producing a
 * smaller distribution and a reconciliation gap on WOs that have
 * approved change orders.
 */
export function applyCCFeeToRolledItems(items, ccFeePct, feeOverride = null) {
  const feePct = Math.max(0, parseFloat(ccFeePct) || 0);
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return list;
  const explicitFee = feeOverride != null && Number.isFinite(parseFloat(feeOverride))
    ? Math.max(0, parseFloat(feeOverride))
    : null;
  if (feePct <= 0 && (explicitFee == null || explicitFee <= 0)) return list;
  const subtotal = list.reduce((s, x) => s + (Number(x.total) || 0), 0);
  if (subtotal <= 0 && explicitFee == null) return list;
  const feeAmount = explicitFee != null ? explicitFee : (subtotal * feePct) / 100;
  if (feeAmount <= 0) return list;
  const matSum = list.reduce((s, x) => s + (Number(x.material_total) || 0), 0);
  if (matSum > 0) {
    return list.map(r => {
      const share = ((Number(r.material_total) || 0) / matSum) * feeAmount;
      return { ...r, material_total: (Number(r.material_total) || 0) + share, total: (Number(r.total) || 0) + share };
    });
  }
  // No material on any row — fold the entire fee into the first row.
  return list.map((r, i) => i === 0
    ? { ...r, material_total: (Number(r.material_total) || 0) + feeAmount, total: (Number(r.total) || 0) + feeAmount }
    : r);
}
